import { Alert, Button } from '@arco-design/web-react';
import type { FormSessionToken } from '@jukun/platform-sdk/shell';
import type { WorkflowSaveCapability, WorkflowWorkItemBridge } from '@jukun/platform-sdk/workflow';
import { useLayoutEffect } from 'react';
import { PortalFormSessionHost } from '../../../components/PortalFormSessionHost';
import { usePortalFormSessionManager, usePortalFormSessionSnapshot } from '../../../runtime/formSessionContext';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import type { PortalWorkItemDetail } from '../model/myTasks';
import { hasLocalWorkItemSave } from '../runtime/taskFormSelection';

export const WORK_ITEM_MOUNT_TIMEOUT_MS = 20_000;
export const WORK_ITEM_READY_TIMEOUT_MS = 8_000;
export type WorkItemFormRuntimeState = Readonly<{
  mountId: string; taskId: string; token?: FormSessionToken;
} & (
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; saveCapability: WorkflowSaveCapability; bridge?: WorkflowWorkItemBridge }
)>;

/** The Shell prepares the session; this component only owns its displayed position. */
export function WorkItemFormHost(props: {
  detail: PortalWorkItemDetail; token?: FormSessionToken; sourceFailure?: unknown;
  applications: PortalApplication[]; identity: PortalIdentity;
  onRuntimeChange: (state: WorkItemFormRuntimeState) => void;
  onSessionExpired: () => void; onRetry: () => void;
}) {
  const manager = usePortalFormSessionManager();
  const session = usePortalFormSessionSnapshot().activeSession;
  const matches = !!props.token && session?.token.sessionId === props.token.sessionId
    && session.snapshot.context.contextType === 'WORK_ITEM'
    && session.snapshot.context.workflowTaskPublicId === props.detail.summary.taskId;
  const bridge = matches && session.status === 'READY' ? manager.getBridge(session.token) : undefined;
  const workItemBridge = bridge?.contextType === 'WORK_ITEM' ? bridge.bridge : undefined;
  const writable = props.detail.flowPageContext.recipientType === 'TODO';
  const saveEligible = !!matches && session.status === 'READY' && !!workItemBridge
    && hasLocalWorkItemSave(props.detail);
  useLayoutEffect(() => {
    if (!matches || !props.token || session?.status === 'FAILED') return;
    manager.setSaveEligibility(props.token, saveEligible);
  }, [manager, matches, props.token?.sessionId, saveEligible, session?.status]);
  const status = props.sourceFailure || matches && session?.status === 'FAILED' ? 'failed'
    : matches && session?.status === 'READY' ? 'ready' : 'loading';
  useLayoutEffect(() => {
    const identity = { mountId: props.token?.sessionId ?? `unavailable:${props.detail.summary.taskId}`,
      taskId: props.detail.summary.taskId, token: props.token };
    props.onRuntimeChange(status === 'ready'
      ? { ...identity, status, saveCapability: writable && workItemBridge ? 'REQUIRED' : 'NONE',
        bridge: writable ? workItemBridge : undefined }
      : { ...identity, status });
  }, [props.onRuntimeChange, props.detail.summary.taskId, props.token?.sessionId, status, writable, workItemBridge]);
  return <div className="portal-work-item-form-host">
    {props.sourceFailure && !props.token ? <Alert type="error" title="来源表单暂不可用"
      content="任务信息仍可查看，请重新加载来源表单。"
      action={<Button type="primary" size="small" onClick={props.onRetry}>重新加载</Button>} />
      : <PortalFormSessionHost token={props.token} onRetry={props.onRetry}
        className="portal-work-item-form-host__container" />}
  </div>;
}
